import {
  createAddOn as publicCreateAddOn,
  getAddOn as publicGetAddOn,
  updateAddOn as publicUpdateAddOn,
  deleteAddOn as publicDeleteAddOn,
  queryAddOns as publicQueryAddOns,
  typedQueryAddOns as publicTypedQueryAddOns,
  bulkCreateAddOns as publicBulkCreateAddOns,
  bulkUpdateAddOns as publicBulkUpdateAddOns,
  bulkDeleteAddOns as publicBulkDeleteAddOns,
} from './addons-v1-add-on-add-ons.public.js';
import { createRESTModule } from '@wix/sdk-runtime/rest-modules';
import { createEventModule } from '@wix/sdk-runtime/event-definition-modules';
import {
  BuildRESTFunction,
  MaybeContext,
  BuildEventDefinition,
} from '@wix/sdk-types';
import { HttpClient } from '@wix/sdk-types';
import { createQueryOverloadRouter } from '@wix/sdk-runtime/query-method-router';
import {
  AddOnQuery,
  AddOnsQueryBuilder,
  typedQueryAddOns as universalTypedQueryAddOns,
} from './addons-v1-add-on-add-ons.universal.js';
import { onAddOnCreated as publicOnAddOnCreated } from './addons-v1-add-on-add-ons.public.js';
import { onAddOnDeleted as publicOnAddOnDeleted } from './addons-v1-add-on-add-ons.public.js';
import { onAddOnUpdated as publicOnAddOnUpdated } from './addons-v1-add-on-add-ons.public.js';

function customQueryAddOns(httpClient: HttpClient) {
  const router = createQueryOverloadRouter({
    builderQueryFunction: () => publicQueryAddOns(httpClient)(),
    typedQueryFunction: (query: AddOnQuery) =>
      publicTypedQueryAddOns(httpClient)(query),
    hasOptionsParameter: false,
  });

  function overloadedQuery(): AddOnsQueryBuilder;
  function overloadedQuery(
    query: AddOnQuery
  ): ReturnType<typeof universalTypedQueryAddOns>;
  function overloadedQuery(query?: AddOnQuery): any {
    return router(...arguments);
  }

  return overloadedQuery;
}

export const createAddOn: MaybeContext<
  BuildRESTFunction<typeof publicCreateAddOn> & typeof publicCreateAddOn
> = /*#__PURE__*/ createRESTModule(publicCreateAddOn);
export const getAddOn: MaybeContext<
  BuildRESTFunction<typeof publicGetAddOn> & typeof publicGetAddOn
> = /*#__PURE__*/ createRESTModule(publicGetAddOn);
export const updateAddOn: MaybeContext<
  BuildRESTFunction<typeof publicUpdateAddOn> & typeof publicUpdateAddOn
> = /*#__PURE__*/ createRESTModule(publicUpdateAddOn);
export const deleteAddOn: MaybeContext<
  BuildRESTFunction<typeof publicDeleteAddOn> & typeof publicDeleteAddOn
> = /*#__PURE__*/ createRESTModule(publicDeleteAddOn);
export const bulkCreateAddOns: MaybeContext<
  BuildRESTFunction<typeof publicBulkCreateAddOns> &
    typeof publicBulkCreateAddOns
> = /*#__PURE__*/ createRESTModule(publicBulkCreateAddOns);
export const bulkUpdateAddOns: MaybeContext<
  BuildRESTFunction<typeof publicBulkUpdateAddOns> &
    typeof publicBulkUpdateAddOns
> = /*#__PURE__*/ createRESTModule(publicBulkUpdateAddOns);
export const bulkDeleteAddOns: MaybeContext<
  BuildRESTFunction<typeof publicBulkDeleteAddOns> &
    typeof publicBulkDeleteAddOns
> = /*#__PURE__*/ createRESTModule(publicBulkDeleteAddOns);
export const queryAddOns: MaybeContext<
  BuildRESTFunction<typeof customQueryAddOns> & typeof customQueryAddOns
> = /*#__PURE__*/ createRESTModule(customQueryAddOns);
/**
 * Triggered when an add-on is created.
 */
export const onAddOnCreated: BuildEventDefinition<typeof publicOnAddOnCreated> &
  typeof publicOnAddOnCreated = createEventModule(publicOnAddOnCreated);
/**
 * Triggered when an add-on is deleted.
 */
export const onAddOnDeleted: BuildEventDefinition<typeof publicOnAddOnDeleted> &
  typeof publicOnAddOnDeleted = createEventModule(publicOnAddOnDeleted);
/**
 * Triggered when an add-on is updated.
 */
export const onAddOnUpdated: BuildEventDefinition<typeof publicOnAddOnUpdated> &
  typeof publicOnAddOnUpdated = createEventModule(publicOnAddOnUpdated);

export {
  SortOrder,
  PlacementType,
  DayOfWeek,
  ResolutionMethod,
  WebhookIdentityType,
} from './addons-v1-add-on-add-ons.universal.js';
export {
  AddOn,
  AddOnAddOnInfoOneOf,
  Money,
  ExtendedFields,
  Tags,
  TagList,
  CreateAddOnRequest,
  CreateAddOnResponse,
  GetAddOnRequest,
  GetAddOnResponse,
  UpdateAddOnRequest,
  UpdateAddOnResponse,
  DeleteAddOnRequest,
  DeleteAddOnResponse,
  QueryAddOnsRequest,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Sorting,
  CursorPaging,
  QueryAddOnsResponse,
  CursorPagingMetadata,
  Cursors,
  QueryLocalizedAddOnsRequest,
  QueryLocalizedAddOnsResponse,
  BulkCreateAddOnsRequest,
  BulkCreateAddOnsResponse,
  ItemMetadata,
  ApplicationError,
  BulkAddOnResult,
  BulkActionMetadata,
  BulkUpdateAddOnsRequest,
  MaskedAddOn,
  BulkUpdateAddOnsResponse,
  BulkUpdateAddOnsResponseBulkAddOnResult,
  BulkDeleteAddOnsRequest,
  BulkDeleteAddOnsResponse,
  BulkDeleteAddOnsResponseBulkAddOnResult,
  BulkUpdateAddOnTagsRequest,
  BulkUpdateAddOnTagsResponse,
  BulkUpdateAddOnTagsResult,
  BulkUpdateAddOnTagsByFilterRequest,
  BulkUpdateAddOnTagsByFilterResponse,
  SitePropertiesNotification,
  SitePropertiesEvent,
  Properties,
  Categories,
  Locale,
  Address,
  AddressHint,
  GeoCoordinates,
  BusinessSchedule,
  TimePeriod,
  SpecialHourPeriod,
  Multilingual,
  SupportedLanguage,
  ConsentPolicy,
  Translation,
  ChangeContext,
  ChangeContextPayloadOneOf,
  PropertiesChange,
  SiteCreated,
  SiteCloned,
  Empty,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  RestoreInfo,
  EntityUpdatedEvent,
  EntityDeletedEvent,
  ActionEvent,
  MessageEnvelope,
  IdentificationData,
  IdentificationDataIdOneOf,
  AccountInfo,
  BaseEventMetadata,
  EventMetadata,
  AccountInfoMetadata,
  AddOnCreatedEnvelope,
  AddOnDeletedEnvelope,
  AddOnUpdatedEnvelope,
  UpdateAddOn,
  AddOnsQueryResult,
  AddOnsQueryBuilder,
  AddOnQuerySpec,
  BulkCreateAddOnsOptions,
  BulkUpdateAddOnsOptions,
} from './addons-v1-add-on-add-ons.universal.js';
export { utils } from './addons-v1-add-on-add-ons.universal.js';
export {
  SortOrderWithLiterals,
  PlacementTypeWithLiterals,
  DayOfWeekWithLiterals,
  ResolutionMethodWithLiterals,
  WebhookIdentityTypeWithLiterals,
  CommonQueryWithEntityContext,
  AddOnQuery,
} from './addons-v1-add-on-add-ons.universal.js';
